import {Button, Group, Stack, Text} from '@mantine/core';
import React from 'react';

import {isColumnTypeCompatible} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import {DataFrameColumnDef, DataFrameSpec} from 'data-frames/DataFrameTypes';
import getUserColumns from 'data-frames/util/getUserColumns';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {FIELD_TYPE_TO_LEAF_TYPE} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {JSONValue} from 'util/Types';

import {maybeGetValueFromConfig, setValueInConfig} from '../../step-config-settings/StepConfigSettingsUtils';
import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../../StepConfigEditorContext';
import {ConfigShapePath, CustomComponentEditorProps} from '../../StepConfigEditorTypes';
import ColumnMappingGrid, {ColumnMappingRow} from './ColumnMappingGrid';

const FIELD_TYPE_TO_LEAF_TYPE_BY_STRING: Record<string, LeafType | undefined> = FIELD_TYPE_TO_LEAF_TYPE;

/**
 * Structural suffix words that appear across many mapping fields (`id`, `unit`, `version`, …) and instrument
 * headers. They carry almost no signal about which column a field means, so they are matched but not counted
 * toward the "meaningful overlap" that qualifies a match. Without this, e.g. `transfer_unit_id` would map to
 * a `TransferID` row-id column purely on the shared `id`/`transfer` tokens.
 */
const GENERIC_TOKENS = new Set([
  'id',
  'ids',
  'index',
  'idx',
  'position',
  'pos',
  'amount',
  'amt',
  'unit',
  'units',
  'version',
  'ver',
  'num',
  'number',
  'type',
  'value',
  'val',
  'code',
  'name',
  'key',
  'ref',
]);

/** Minimum number of meaningful (non-generic) shared tokens required to auto-map a field to a column. */
const MIN_MEANINGFUL_TOKEN_OVERLAP = 2;

/** Shortest token length eligible for prefix-abbreviation matching (so `dest`↔`destination`, but not `id`). */
const MIN_PREFIX_MATCH_LENGTH = 4;

/**
 * Break a name into lowercase word tokens, splitting on camelCase boundaries, letter/digit boundaries, and
 * any non-alphanumeric separator. `"SourcePlateBarcode"` and `"source_plate_id"` both tokenize to word lists
 * that can be compared directly.
 */
function tokenize(value: string): Array<string> {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])([0-9])/g, '$1 $2')
    .replace(/([0-9])([A-Za-z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .map((token) => token.toLowerCase())
    .filter((token) => token.length > 0);
}

/** Two tokens match if equal, or if the shorter is a prefix of the longer (handling abbreviations like dest). */
function tokensMatch(a: string, b: string): boolean {
  if (a === b) {
    return true;
  }
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  return shorter.length >= MIN_PREFIX_MATCH_LENGTH && longer.startsWith(shorter);
}

/** The distinct meaningful (non-generic) tokens in a name. */
function meaningfulTokens(tokens: Array<string>): Array<string> {
  return [...new Set(tokens)].filter((token) => !GENERIC_TOKENS.has(token));
}

/** Count how many distinct meaningful field tokens have a matching meaningful column token. */
function countMeaningfulOverlap(
  fieldMeaningfulTokens: Array<string>,
  columnMeaningfulTokens: Array<string>
): number {
  const usedColumnTokens = new Set<number>();
  let overlap = 0;
  for (const fieldToken of fieldMeaningfulTokens) {
    const matchIndex = columnMeaningfulTokens.findIndex(
      (columnToken, index) => !usedColumnTokens.has(index) && tokensMatch(fieldToken, columnToken)
    );
    if (matchIndex !== -1) {
      usedColumnTokens.add(matchIndex);
      overlap += 1;
    }
  }
  return overlap;
}

/**
 * The leaf types a field will accept, or `null` when the field imposes no type constraint. A column-reference
 * row with no validation options accepts any column type (matching the dropdown), so it must not be narrowed
 * to TEXT; only a schema field's declared type or explicit `allowedLeafTypes` restricts the candidates.
 */
function fieldAllowedLeafTypes(row: ColumnMappingRow): Array<LeafType> | null {
  if (row.allowedLeafTypes != null && row.allowedLeafTypes.length > 0) {
    return [...row.allowedLeafTypes];
  }
  if (row.fieldType != null) {
    return [FIELD_TYPE_TO_LEAF_TYPE_BY_STRING[row.fieldType] ?? LeafType.TEXT];
  }
  return null;
}

/** Whether a column's type satisfies any of the leaf types a field accepts (always true when unconstrained). */
function columnSatisfiesFieldType(column: DataFrameColumnDef, row: ColumnMappingRow): boolean {
  const allowedLeafTypes = fieldAllowedLeafTypes(row);
  if (allowedLeafTypes == null) {
    return true;
  }
  return allowedLeafTypes.some((leafType) =>
    isColumnTypeCompatible(column, {type: {leafType}}, {alwaysAllowNullable: true})
  );
}

interface ScoredMatch {
  row: ColumnMappingRow;
  column: DataFrameColumnDef;
  overlap: number;
  /**
   * Fraction of the field's and column's meaningful tokens that matched (Jaccard-style). Breaks ties between
   * fields competing for the same column: a field whose name is fully explained by the column beats one that
   * merely shares as many tokens but carries extra unmatched ones (e.g. "Destination plate index" beats
   * "Source destination plate index" for a "DestPlateIdx" column).
   */
  coverage: number;
}

/**
 * Match empty single-select rows to input columns by shared, meaningful name tokens.
 *
 * A row is only matched to a type-compatible column that shares at least MIN_MEANINGFUL_TOKEN_OVERLAP
 * meaningful tokens, or whose full name matches exactly. Matches are assigned best-first — by overlap, then by
 * coverage — so the clearest pairings win, and each row and column is used at most once. Fields with no
 * confident match are left empty rather than filled with a best-effort guess.
 */
function autoMapRowsToColumns(
  emptyRows: Array<ColumnMappingRow>,
  availableColumns: Array<DataFrameColumnDef>
): Array<{row: ColumnMappingRow; column: DataFrameColumnDef}> {
  const columnTokensBySystemName = new Map(
    availableColumns.map((column) => [column.systemName, tokenize(column.displayName)])
  );
  const candidates: Array<ScoredMatch> = [];
  for (const row of emptyRows) {
    const fieldTokens = tokenize(row.displayName);
    const fieldMeaningful = meaningfulTokens(fieldTokens);
    const normalizedField = fieldTokens.join('');
    for (const column of availableColumns) {
      if (!columnSatisfiesFieldType(column, row)) {
        continue;
      }
      const columnTokens = columnTokensBySystemName.get(column.systemName)!;
      const columnMeaningful = meaningfulTokens(columnTokens);
      const overlap = countMeaningfulOverlap(fieldMeaningful, columnMeaningful);
      const isExactNameMatch = normalizedField.length > 0 && normalizedField === columnTokens.join('');
      if (overlap >= MIN_MEANINGFUL_TOKEN_OVERLAP || isExactNameMatch) {
        const distinctMeaningful = new Set([...fieldMeaningful, ...columnMeaningful]).size;
        candidates.push({
          row,
          column,
          overlap: isExactNameMatch ? Number.MAX_SAFE_INTEGER : overlap,
          coverage: distinctMeaningful === 0 ? 1 : overlap / distinctMeaningful,
        });
      }
    }
  }

  candidates.sort((a, b) => b.overlap - a.overlap || b.coverage - a.coverage);
  const usedColumns = new Set<string>();
  const assignedRows = new Set<string>();
  const result: Array<{row: ColumnMappingRow; column: DataFrameColumnDef}> = [];
  for (const {row, column} of candidates) {
    if (assignedRows.has(row.id) || usedColumns.has(column.systemName)) {
      continue;
    }
    assignedRows.add(row.id);
    usedColumns.add(column.systemName);
    result.push({row, column});
  }
  return result;
}

/** The column system names a row currently references, whether it is a single- or multi-select mapping. */
function selectedColumnNames(config: Record<string, JSONValue>, row: ColumnMappingRow): Array<string> {
  const value = maybeGetValueFromConfig(config, row.targetPath);
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string' && entry !== '');
  }
  return typeof value === 'string' && value !== '' ? [value] : [];
}

/**
 * Build a config that fills confidently-matched empty single-select rows with their input column.
 *
 * Only empty rows are considered so an explicit selection is never overwritten. Columns already consumed by
 * any row — single- or multi-select — are excluded so nothing is mapped twice, and only user columns are
 * offered so auto-map never picks a hidden system column the dropdown would not let the user choose or repair.
 */
function buildAutoMappedConfig(
  config: Record<string, JSONValue>,
  rows: Array<ColumnMappingRow>,
  dataFrameSpec: DataFrameSpec
): Record<string, JSONValue> {
  const usedSourceNames = new Set(rows.flatMap((row) => selectedColumnNames(config, row)));
  const emptyRows = rows.filter((row) => !row.multiSelect && selectedColumnNames(config, row).length === 0);
  const availableColumns = getUserColumns(dataFrameSpec).filter(
    (column) => !usedSourceNames.has(column.systemName)
  );
  return autoMapRowsToColumns(emptyRows, availableColumns).reduce(
    (nextConfig, {row, column}) => setValueInConfig(nextConfig, row.targetPath, column.systemName),
    config
  );
}

function titleCase(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^\w/, (char) => char.toUpperCase());
}

function inputDataNameForSpec(spec: PipelineConfigJsonSpec): string {
  if (spec.kind === 'columnReference') {
    return spec.inputDataName;
  }
  if (spec.kind === 'array' && spec.elementSpec.kind === 'columnReference') {
    return spec.elementSpec.inputDataName;
  }
  return '';
}

function rowsForSubfield(
  mappingPath: ConfigShapePath,
  subfieldName: string,
  spec: PipelineConfigJsonSpec,
  subfieldDisplayNames: Record<string, string>,
  subfieldInfoText: Record<string, string>
): Array<ColumnMappingRow> {
  if (spec.kind === 'columnReference') {
    return [
      {
        id: subfieldName,
        displayName: subfieldDisplayNames[subfieldName] ?? titleCase(subfieldName),
        targetPath: [...mappingPath, subfieldName],
        required: spec.required,
        multiSelect: false,
        allowedLeafTypes: spec.validationOptions?.allowedLeafTypes,
        infoText: subfieldInfoText[subfieldName],
      },
    ];
  }
  if (spec.kind === 'array' && spec.elementSpec.kind === 'columnReference') {
    return [
      {
        id: subfieldName,
        displayName: subfieldDisplayNames[subfieldName] ?? titleCase(subfieldName),
        targetPath: [...mappingPath, subfieldName],
        required: spec.required,
        multiSelect: true,
        allowedLeafTypes: spec.elementSpec.validationOptions?.allowedLeafTypes,
        infoText: subfieldInfoText[subfieldName],
      },
    ];
  }
  if (spec.kind === 'schemaFields') {
    const resolved = spec.__resolvedRows ?? [];
    return resolved.map((row) => ({
      id: `${subfieldName}.${row.systemName}`,
      displayName: row.displayName,
      targetPath: [...mappingPath, subfieldName, row.systemName],
      required: row.required ?? false,
      multiSelect: false,
      fieldType: row.fieldType,
    }));
  }
  return [];
}

export default function ColumnMappingEditor({
  config,
  configShape,
  configShapePath,
  onChangeConfig,
  disabled,
  columnSourceInputDataName,
  subfieldDisplayNames = {},
  subfieldInfoText = {},
}: CustomComponentEditorProps & {
  configShapePath: ConfigShapePath;
  columnSourceInputDataName?: string;
  subfieldDisplayNames?: Record<string, string>;
  subfieldInfoText?: Record<string, string>;
}): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();

  if (configShape.kind !== 'columnMapping') {
    return <></>;
  }

  const schemaInaccessible = Object.values(configShape.fields).some(
    (spec) => spec.kind === 'schemaFields' && spec.__schemaInaccessible === true
  );
  if (schemaInaccessible) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="You don't have permission to view this schema's fields, so its column mapping can't be shown or edited."
      />
    );
  }

  const rows = Object.entries(configShape.fields).flatMap(([subfieldName, spec]) =>
    rowsForSubfield(configShapePath, subfieldName, spec, subfieldDisplayNames, subfieldInfoText)
  );

  const inputDataName =
    columnSourceInputDataName ??
    Object.values(configShape.fields)
      .map((spec) => inputDataNameForSpec(spec))
      .find((name) => name !== '') ??
    '';
  const {dataFrameSpec} = getDataFrameInfoOrEmptyInfo(dataFramesInfos, inputDataName);

  if (rows.length === 0) {
    return (
      <Text color="dimmed" size="sm">
        Select a schema to map its fields to source columns.
      </Text>
    );
  }

  const hasEmptySingleSelectRow = rows.some(
    (row) => !row.multiSelect && maybeGetValueFromConfig(config, row.targetPath) == null
  );
  const canAutoMap = !disabled && getUserColumns(dataFrameSpec).length > 0 && hasEmptySingleSelectRow;

  return (
    <Stack spacing="xs">
      <Group position="right" spacing="xs">
        <Button
          variant="ghost"
          size="xs"
          disabled={!canAutoMap}
          {...getTestProps({component: 'ColumnMappingEditor', element: 'autoMapButton'})}
          onClick={() => {
            onChangeConfig(config, [], buildAutoMappedConfig(config, rows, dataFrameSpec));
          }}
        >
          Auto-map columns
        </Button>
      </Group>
      <ColumnMappingGrid
        rows={rows}
        dataFrameSpec={dataFrameSpec}
        valueForPath={(path: ConfigShapePath) => maybeGetValueFromConfig(config, path)}
        onChange={(path, value: JSONValue) => {
          onChangeConfig(config, path, value);
        }}
        disabled={disabled}
      />
    </Stack>
  );
}
